import React from 'react';
import { SectionCard } from '../../components/sections/SectionCard';
import { PanelTable } from '../../components/display/PanelTable';
import { AppStatusBadge } from '@shared/components/ui/status-badge/AppStatusBadge';
import { formatCurrency, formatDate } from '@shared/utils/formatters';
import type { PanelTableColumn } from '../../components/display/PanelTable/types';
import { mockRefunds } from '@shared/data/mockRefunds';
import type { Refund } from '@shared/types/refunds';

interface RefundsSectionProps {
  isDetailView?: boolean;
}

const renderAmount = (_: unknown, refund: Refund) => (
  <div className="font-mono text-sm">
    {formatCurrency(refund.amount, refund.currency)}
  </div>
);

const renderStatus = (_: unknown, refund: Refund) => (
  <AppStatusBadge entityType="refund" status={refund.status} size="sm" />
);

const renderReason = (_: unknown, refund: Refund) => {
  const reasonLabels: Record<string, string> = {
    'requested_by_customer': 'Customer Request',
    'duplicate': 'Duplicate',
    'fraudulent': 'Fraudulent',
    'product_not_received': 'Product Not Received',
    'product_unacceptable': 'Product Unacceptable',
    'other': 'Other'
  };

  return (
    <span className="text-xs text-muted-foreground">
      {reasonLabels[refund.reason] || refund.reason}
    </span>
  );
};

const renderDate = (_: unknown, refund: Refund) => (
  <span className="text-xs">{formatDate(refund.created)}</span>
);

const renderPayment = (_: unknown, refund: Refund) => (
  <span className="text-xs font-mono text-muted-foreground">
    {refund.paymentId}
  </span>
);

export const RefundsSection = React.memo<RefundsSectionProps>(({ isDetailView = false }) => {
  const columns: PanelTableColumn<Refund>[] = [
    {
      key: 'amount',
      label: 'AMOUNT',
      render: renderAmount,
      headerClassName: 'w-[120px]'
    },
    {
      key: 'status',
      label: 'STATUS',
      render: renderStatus,
      headerClassName: 'w-[110px]'
    },
    {
      key: 'reason',
      label: 'REASON',
      render: renderReason,
      headerClassName: 'w-[150px]'
    },
    {
      key: 'paymentId',
      label: 'PAYMENT',
      render: renderPayment,
      headerClassName: 'w-[140px]'
    },
    {
      key: 'created',
      label: 'CREATED',
      render: renderDate,
      headerClassName: 'w-[120px]'
    },
  ];

  const rowActions = (refund: Refund) => {
    const actions = [
      {
        key: 'view-refund',
        label: 'View refund',
        onClick: () => console.log('View refund:', refund.id)
      },
      {
        key: 'view-payment',
        label: 'View original payment',
        onClick: () => console.log('View payment:', refund.paymentId)
      },
    ];

    // Add cancel action for pending refunds
    if (refund.status === 'pending') {
      actions.push({
        key: 'cancel-refund',
        label: 'Cancel refund',
        onClick: () => console.log('Cancel refund:', refund.id)
      });
    }

    return actions;
  };

  return (
    <SectionCard title="Refunds" layout="down">
      <PanelTable
        data={mockRefunds}
        columns={columns}
        variant={isDetailView ? 'full' : 'panel'}
        emptyMessage="No refunds found for this customer"
        pageSize={5}
        rowActions={rowActions}
      />
    </SectionCard>
  );
});

RefundsSection.displayName = 'RefundsSection';
